import { DestroyRef, inject, Injectable, Injector, StaticProvider } from '@angular/core';
import {
  AnimationAnimateMetadata,
  AnimationBuilder,
  AnimationPlayer,
  AnimationStyleMetadata
} from '@angular/animations';
import { BreakpointObserverService, NavigationService } from '@pc/shared';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/overlay';
import { TranslationService } from '@pc/translation';
import {
  ANIMATION_TIME,
  desktopCloseAnimation,
  desktopOpenAnimation,
  mobileCloseAnimation,
  mobileOpenAnimation
} from '@pc/animations';

@Injectable({
  providedIn: 'root'
})
export class AnimationDialogService {
  private readonly injector = inject(Injector);
  private readonly breakpoint = inject(BreakpointObserverService);
  private readonly destroy = inject(DestroyRef);
  private animationBuilder = inject(AnimationBuilder);
  private translationService = inject(TranslationService);
  private navigationService = inject(NavigationService);
  private player: AnimationPlayer | null = null;
  private isDesktop = this.breakpoint.getMediumBreakpoint(this.injector);
  private timeOutId?: ReturnType<typeof setTimeout>;

  constructor() {
    this.destroy.onDestroy(() => {
      clearTimeout(this.timeOutId);
    });
  }

  public closeDialogWithAnimation<T = unknown>(dialogRef: DialogRef, data?: T): void {
    this.doAnimation(mobileCloseAnimation, desktopCloseAnimation);
    this.timeOutId = setTimeout(() => {
      dialogRef.close(data);
    }, ANIMATION_TIME - 50);
    this.navigationService.isLocked = false;
  }

  public openDialogWithAnimation<C = unknown, T = unknown>(
    dialogRef: Dialog,
    component: ComponentType<C>,
    event?: T,
    providers?: StaticProvider[],
    disableClose = false,
    autoFocus?: boolean
  ): DialogRef<unknown, C> {
    const dialog = dialogRef.open(component, {
      direction: this.translationService.direction(),
      data: { event },
      providers: providers || [],
      disableClose: disableClose,
      autoFocus: autoFocus
    });
    this.doAnimation(mobileOpenAnimation, desktopOpenAnimation);
    this.navigationService.isLocked = disableClose;
    return dialog;
  }

  private doAnimation(
    mobileAnimation: (AnimationStyleMetadata | AnimationAnimateMetadata)[],
    desktopAnimation: (AnimationStyleMetadata | AnimationAnimateMetadata)[]
  ): void {
    const dialogElement = document.getElementById('dialogElement');
    if (dialogElement) {
      const animation = this.animationBuilder?.build(!this.isDesktop() ? mobileAnimation : desktopAnimation);
      this.player = animation?.create(dialogElement);
      this.player.play();
      this.player.onDone(() => {
        this.player?.finish();
        this.player?.destroy();
        this.player = null;
      });
    }
  }
}
